import fromPairs from 'lodash/fromPairs';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { BuiltInComponents } from 'craft/constants';
import { useMutations, useNodeContext, useNodeCustom, useSelection } from 'craft/hooks';
import { craftToSlateMarks, slateToCraftMarks } from 'craft/rte/constants';
import { Resolver } from 'craft/types';

// Empty nodes need pseudo content so they have height
// https://github.com/candulabs/service-backend/pull/1199
const StyledTextNode = styled.span`
  &:empty:before {
    content: '';
    display: inline-block;
  }
`;

export const Text: Resolver = (props: any) => {
  const { id: nodeId } = useNodeContext();
  const custom = useNodeCustom(nodeId);
  const { all } = useSelection().selection;
  const { deleteNode } = useMutations();

  const marks = custom.custom?.marks || [];
  const hasMark = (mark: string) => marks.indexOf(mark) > -1;

  useEffect(() => {
    if (!props.childrenString) {
      deleteNode([nodeId]);
    }
  }, [all]);

  let result = props.childrenString;
  if (hasMark('B')) {
    result = <StyledTextNode as="strong">{result}</StyledTextNode>;
  }

  if (hasMark('InlineCode')) {
    result = <StyledTextNode as="code">{result}</StyledTextNode>;
  }

  if (hasMark('Em')) {
    result = <StyledTextNode as="em">{result}</StyledTextNode>;
  }

  if (hasMark('U')) {
    result = <StyledTextNode as="u">{result}</StyledTextNode>;
  }

  return <StyledTextNode>{result}</StyledTextNode>;
};

Text.craft = {
  name: BuiltInComponents.Text,
};

const getCraftMarks = (node: any) => {
  return Object.keys(node)
    .map((key) => slateToCraftMarks[key])
    .filter((mark) => !!mark);
};

const getSlateMarks = (marks: any) => {
  if (!marks || !Array.isArray(marks) || marks.length < 1) {
    return {};
  }

  // @ts-ignore
  return fromPairs(marks.map((mark) => [craftToSlateMarks[mark], true]));
};

/* eslint-disable no-param-reassign */
Text.slate = {
  toCraftNode: (slateNode: any) => (craftNode: any) => {
    craftNode.data.custom = {
      ...(craftNode.data.custom || {}),
      custom: {
        ...(craftNode.data.custom.custom || {}),
        marks: getCraftMarks(slateNode),
      },
    };

    if (slateNode.variable) {
      craftNode.data.custom.variables = [
        {
          ...slateNode.variable,
        },
      ];
    }

    craftNode.data.props.childrenString = slateNode.text || '';
  },
  toSlateNode: (craftNode: any) => (slateNode: any) => {
    Object.keys(getSlateMarks(craftNode.data.custom.custom?.marks)).forEach((mark) => {
      slateNode[mark] = true;
    });

    if (craftNode.data.custom.variables && craftNode.data.custom.variables[0]) {
      // eslint-disable-next-line prefer-destructuring
      slateNode.variable = craftNode.data.custom.variables[0];
    }

    slateNode.text = craftNode.data.props.childrenString || '';
  },
};
